import {
  Component,
  EventEmitter,
  Output,
  OnInit,
  ViewChild,
  Input,
  OnChanges,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'

import {
  ReactiveFormsModule,
  FormBuilder,
  FormsModule,
  Validators,
  FormArray,
  FormGroup,
} from '@angular/forms'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatIcon } from '@angular/material/icon'
import { MatChipsModule } from '@angular/material/chips'
import { MatDivider } from '@angular/material/divider'
import { MatButtonModule } from '@angular/material/button'
import { MatRadioModule } from '@angular/material/radio'
import { MatSelectChange, MatSelectModule } from '@angular/material/select'
import { ROUTES } from '../../../constants/routes'
import { RouteService } from '../../../services/route.service'
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { debounceTime, firstValueFrom, Subject, switchMap } from 'rxjs'
import {
  Ingredient,
  IngredientFormData,
  IngredientFormRow,
  IngredientListData,
} from '../../../types/ingredients'
import { IngredientService } from '../../../services/ingredient.service'
import { TranslationService } from '../../../services/translation.service'
import { Role } from '../../../types/roles'
import { RoleService } from '../../../services/role.service'
import { DialogService } from '../../../services/dialog.service'
import { Dialog } from '@angular/cdk/dialog'
import { SpinnerService } from '../../../services/spinner.service'
import { ErrorService } from '../../../services/error.service'
import { ApiError } from '../../../types/apiError'
@Component({
  selector: 'app-product-ingredients-form',
  imports: [
    MatFormField,
    MatLabel,
    MatInputModule,
    ReactiveFormsModule,
    MatIcon,
    MatChipsModule,
    MatDivider,
    MatButtonModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatCheckboxModule,
    FormsModule,
    MatDivider,
    MatRadioModule,
  ],
  templateUrl: './product-ingredients-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './product-ingredients-form.component.scss',
})
export class ProductIngredientsFormComponent implements OnInit, OnChanges {
  private fb = inject(FormBuilder)
  private ingredientService = inject(IngredientService)
  private routeService = inject(RouteService)
  translationService = inject(TranslationService)
  private roleService = inject(RoleService)
  private dialogService = inject(DialogService)
  private dialog = inject(Dialog)
  private spinnerService = inject(SpinnerService)
  private errorService = inject(ErrorService)

  @Input() clonedData: IngredientFormData | undefined
  @Output() formChanged = new EventEmitter<IngredientFormData>()
  @Output() backPressed = new EventEmitter()
  @ViewChild(MatAutocompleteTrigger) autocomplete!: MatAutocompleteTrigger

  ingredientQueryChanged: Subject<{ query: string; index?: number }> =
    new Subject<{ query: string; index?: number }>()

  filteredOptions: Array<Array<Ingredient>> = []

  rangeCodes: Array<{ code: number; upperBound: number; lowerBound: number }> =
    [
      { code: 1, upperBound: 100, lowerBound: 30 },
      { code: 2, upperBound: 30, lowerBound: 10 },
      { code: 3, upperBound: 10, lowerBound: 3 },
      { code: 4, upperBound: 3, lowerBound: 1 },
      { code: 5, upperBound: 1, lowerBound: 0.3 },
      { code: 6, upperBound: 0.3, lowerBound: 0.1 },
      { code: 7, upperBound: 0.1, lowerBound: 0 },
    ]
  ingredientForm = this.fb.group({
    ingredients: this.fb.array([]),
    fullIngredientDisclosure: ['false', Validators.required],
  })

  selectiveDisclosureRoles: Array<Role> = []
  selectiveDisclosureRoleIds: Array<string> = []
  selectedRoles: Array<string> = []

  ingredientNameMap = new Map<string, string>()

  async ngOnInit(): Promise<void> {
    const currentRoute = this.routeService.getUserRoutes(
      ROUTES.COMPANY_PRODUCTS.title,
      `/admin/companies/${ROUTES.COMPANY_PRODUCTS.path}`,
    )
    this.routeService.setRoute(currentRoute)

    // get roles for selective disclosure
    await this.getSelectiveDisclosureRoles()

    if (this.clonedData) {
      this.ingredientForm.patchValue({
        fullIngredientDisclosure: `${this.clonedData.fullIngredientDisclosure}`,
      })
      this.clonedData.ingredients.forEach((ingredient) => {
        this.cloneIngredientRow(ingredient)
      })
    }
    this.ingredientQueryChanged
      .pipe(
        debounceTime(300),
        switchMap(async (event) => {
          const name = event.query
          const index = event.index
          try {
            if (
              (typeof name === 'object' && name !== null) ||
              (name !== null && name.length < 2)
            ) {
              return {
                results: [],
                index,
              }
            }
            const results: Array<Ingredient> | undefined =
              await this.ingredientService
                .autoCompleteIngredients(name)
                .toPromise()

            if (results == null || results.length === 0) {
              const msg = [
                {
                  id: 0,
                  nameWithAliases:
                    this.translationService.getNoIngredientsFoundText(),
                  name: '',
                },
              ]
              return { results: msg, index }
            } else {
              return { results, index }
            }
          } catch {
            const msg = [
              {
                id: 0,
                nameWithAliases:
                  this.translationService.getNoIngredientsFoundText(),
                name: '',
              },
            ]
            return { results: msg, index }
          }
        }),
      )
      .subscribe((res) => {
        const index = res.index
        if (index != null) {
          delete res.index
          const results = res.results.map((element) => {
            return {
              id: element.id,
              nameWithAliases: element.nameWithAliases,
              name: element.name,
            } as Ingredient
          })
          this.filteredOptions[index] = results
        }
      })
  }

  async getSelectiveDisclosureRoles() {
    try {
      this.spinnerService.startSpinner()
      this.selectiveDisclosureRoles = await firstValueFrom(
        this.roleService.getForSelectiveDisclosure(),
      )
      this.selectiveDisclosureRoleIds = this.selectiveDisclosureRoles.map(
        (role) => role.id,
      )

      this.spinnerService.stopSpinner()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['clonedData'] && changes['clonedData'].currentValue) {
      const clonedData = changes['clonedData'].currentValue
      this.ingredientForm.patchValue({
        fullIngredientDisclosure: `${clonedData.fullIngredientDisclosure}`,
      })
      clonedData.ingredients.forEach((ingredient: IngredientFormRow) => {
        this.cloneIngredientRow(ingredient)
      })
    }
  }

  cloneIngredientRow(ingredient?: IngredientFormRow) {
    const ingredientRow = this.fb.group({
      id: [ingredient?.id || null],
      nameWithAliases: [ingredient?.nameWithAliases || '', Validators.required],
      upperConcentration: [
        ingredient?.upperConcentration || '',
        Validators.required,
      ],
      lowerConcentration: [
        ingredient?.lowerConcentration || '',
        Validators.required,
      ],
      mayContain: [ingredient?.mayContain || false, Validators.required],
      optionallyDisclosed: [
        ingredient?.optionallyDisclosed || false,
        Validators.required,
      ],
      doesNotContain: [
        ingredient?.doesNotContain || false,
        Validators.required,
      ],
      disclosureRoleIds: [
        ingredient?.disclosureRoleIds?.filter((roleId: string) =>
          this.selectiveDisclosureRoleIds.includes(roleId),
        ) || JSON.parse(JSON.stringify(this.selectiveDisclosureRoleIds)),
        Validators.required,
      ],
    })
    this.ingredients.push(ingredientRow)
    if (ingredient?.doesNotContain || ingredient?.mayContain) {
      this.disableIngredientFields(this.ingredients.length - 1)
    }
    this.ingredientNameMap.set(ingredient!.nameWithAliases, ingredient!.name)
    this.filteredOptions.push([])
  }
  get ingredients() {
    return this.ingredientForm.get('ingredients') as FormArray
  }

  /**
   * Constructs a new ingredient row and adds it to the list
   */
  addIngredientRow() {
    try {
      const ingredientRow = this.fb.group({
        id: [null],
        nameWithAliases: ['', Validators.required],
        upperConcentration: ['', Validators.required],
        lowerConcentration: ['', Validators.required],
        mayContain: [false, Validators.required],
        optionallyDisclosed: [false, Validators.required],
        doesNotContain: [false, Validators.required],
        disclosureRoleIds: [
          JSON.parse(JSON.stringify(this.selectiveDisclosureRoleIds)),
          Validators.required,
        ],
      })
      this.ingredients.push(ingredientRow)
      this.filteredOptions.push([])
    } catch {
      this.dialogService.showDialog(
        this.dialog,
        this.translationService.getErrorText(),
        this.translationService.getErrorOccurredText(),
      )
    }
  }

  /**
   * Handles ingredient select event
   * @param {MatAutocompleteSelectedEvent} event  Ingredient selection event
   * @param {number} index  Index of the ingredient row
   */
  onSelectionChanged(event: MatAutocompleteSelectedEvent, index: number) {
    const ingredient = event.option.value
    this.ingredientForm.get(['ingredients', index])!.patchValue({
      id: ingredient.id,
      nameWithAliases: ingredient.nameWithAliases,
    })

    this.ingredientNameMap.set(ingredient.nameWithAliases, ingredient.name)
  }

  /**
   * Displays the name of a ingredient
   * @param {Ingredient} ingredient Ingredient to display
   * @returns {string} Ingredient name
   */
  displayName(ingredient: Ingredient | string): string {
    if (typeof ingredient === 'string' || ingredient instanceof String) {
      return `${ingredient}`
    }
    return ingredient && ingredient.nameWithAliases
      ? ingredient.nameWithAliases
      : ''
  }

  /**
   * Handles ingredient change events.
   * @param {string} query - The query string.
   */
  async onIngredientChange(event: KeyboardEvent, index: number) {
    const element = event.currentTarget as HTMLInputElement
    const query = element.value
    // The user typed an ingredient name instead of pre-selecting, so set the id to null
    this.ingredientForm.get(['ingredients', index])!.patchValue({
      id: null,
    })
    this.ingredientQueryChanged.next({ query, index })
  }

  /**
   * Resets form data
   */
  onReset() {
    this.ingredientForm.reset()
  }

  /**
   * Handles changes in the range code selection for an ingredient.
   *
   * This function updates the upper and lower concentration values in the ingredient form
   * based on the selected range code.
   *
   * @param {MatSelectChange} event - The change event from the Material select component.
   * @param {number} index - The index of the ingredient in the form array.
   */
  onRangeCodeChange(event: MatSelectChange, index: number): void {
    // Extract the selected code value from the event
    const code = event.value

    // Find the range object that matches the selected code
    const range = this.rangeCodes.find((element) => {
      return element.code === code
    })

    // If a matching range is found, update the form
    if (range != null) {
      this.ingredientForm.get(['ingredients', index])!.patchValue({
        upperConcentration: `${range.upperBound}`,
        lowerConcentration: `${range.lowerBound}`,
      })
    }
  }
  /**
   * Retrieves a specific ingredient form as a FormGroup.
   * @param {number} index - The index of the ingredient form to retrieve.
   * @returns {FormGroup} The FormGroup for the specified ingredient.
   */
  getIngredientFormAsFormGroup(index: number) {
    return this.ingredientForm.get(['ingredients', index]) as FormGroup
  }

  /**
   * Emits when an ingredient is removed
   * @param index
   */
  public removeIngredient(index: number) {
    this.filteredOptions = this.filteredOptions.filter((_, idx) => {
      return index !== idx
    })
    this.ingredients.removeAt(index)
  }

  /**
   * Emits form data
   */
  async onSubmit() {
    this.ingredientForm.markAllAsTouched()
    if (!this.ingredientForm.valid) {
      return
    }

    const fullIngredientDisclosure = this.ingredientForm.get(
      'fullIngredientDisclosure',
    )?.value
    const ingredients = this.ingredientForm.get('ingredients') as FormArray

    if (
      fullIngredientDisclosure &&
      fullIngredientDisclosure === 'true' &&
      !ingredients.length
    ) {
      this.dialogService.showDialog(
        this.dialog,
        this.translationService.getFullIngredientDisclosureText(),
        this.translationService.getIngredientsRequiredForFullIngredientDisclosureMsg(),
      )

      return
    }

    // we want to send the actual name of the ingredient, not the formatted name with aliases in the request
    const ingredientsToCreate = this.ingredientForm.value as IngredientListData
    for (const productIngredient of ingredientsToCreate.ingredients) {
      const ingredientName = this.ingredientNameMap.get(
        productIngredient.nameWithAliases,
      )
      if (!ingredientName) {
        this.dialogService.showDialog(
          this.dialog,
          this.translationService.getErrorText(),
          this.translationService.getErrorOccurredText(),
        )
        return
      } else {
        productIngredient.name = ingredientName
      }
    }

    this.formChanged.emit({
      formName: 'ingredientForm',
      ...(this.ingredientForm.value as IngredientListData),
    })
  }

  /**
   * Handles change to an ingredient's may contain attribute
   * @param {boolean} checked The boolean value
   * @param {number} index The index of the ingredient element
   */
  onMayContainChange(checked: boolean, index: number) {
    const ingredient = this.getIngredientFormAsFormGroup(index)
    const doesNotContain = ingredient.controls['doesNotContain'].value

    if (doesNotContain && checked) {
      ingredient.controls['doesNotContain'].setValue(false)
    }

    if (checked) {
      this.disableIngredientFields(index)
    } else {
      this.enableIngredientFields(index)
    }
  }

  /**
   * Handles change to an ingredient's does not contain attribute
   * @param checked
   * @param index
   */
  onDoesNotContainChange(checked: boolean, index: number) {
    const ingredient = this.getIngredientFormAsFormGroup(index)
    const mayContain = ingredient.controls['mayContain'].value

    if (mayContain && checked) {
      ingredient.controls['mayContain'].setValue(false)
    }

    if (checked) {
      this.disableIngredientFields(index)
    } else {
      this.enableIngredientFields(index)
    }
  }

  /**
   * Disables ingredient fields concentration ranges
   * @param {number} index The index in the ingredient form
   */
  disableIngredientFields(index: number) {
    const ingredient = this.getIngredientFormAsFormGroup(index)

    ingredient.controls['upperConcentration'].reset()
    ingredient.controls['lowerConcentration'].reset()
    ingredient.controls['upperConcentration'].disable()
    ingredient.controls['lowerConcentration'].disable()
  }

  /**
   * Enables Ingredient fields concentration ranges
   * @param {number} index The index of the ingredient form
   */
  enableIngredientFields(index: number) {
    const ingredient = this.getIngredientFormAsFormGroup(index)
    ingredient.controls['lowerConcentration'].enable()
    ingredient.controls['upperConcentration'].enable()
  }

  /**
   * Toggles (Enable/Disable) a few ingredient fields when they should not be used
   * @param val
   * @param ingredient
   */
  toggleIngredientFields(val: boolean, ingredient: FormGroup) {
    if (ingredient) {
      return ingredient.controls['doesNotContain'].value
    }

    return false
  }

  /**
   * Returns the does not contain value of an ingredient by index
   * @param {number} index Index of the ingredient
   * @returns {boolean} may contain flag
   */
  checkForDoesNotContain(index: number): boolean {
    const ingredient = this.getIngredientFormAsFormGroup(index)
    if (ingredient) {
      return ingredient.controls['doesNotContain'].value
    }

    return false
  }

  /**
   * Returns the may contain value of an ingredient by index
   * @param {number} index Index of the ingredient
   * @returns {boolean} may contain flag
   */
  checkForMayContain(index: number): boolean {
    const ingredient = this.getIngredientFormAsFormGroup(index)
    if (ingredient) {
      const mayContain = ingredient.controls['mayContain'].value
      return mayContain
    }

    return false
  }

  /**
   * Handles the click of the back button
   *
   */
  goBack() {
    this.backPressed.emit(null)
  }
}
